import { toast } from '@heidi/design-system/toast';
import { ADVANCED_EVIDENCE_IN_SESSION_QUOTA } from '@/hooks/useEvidenceInSessionLimits';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ScreenName } from '@heidi/analytics/vocabulary';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import {
  EvidenceQuotaConsumedToastBody,
  type EvidenceQuotaConsumedToastBodyProps,
  EvidenceQuotaConsumedToastResetCaption,
} from './EvidenceQuotaConsumedToastBody';
import { EvidenceQuotaConsumedToastUpgradeButton } from './EvidenceQuotaConsumedToastUpgradeButton';
import { track } from '@/lib/analytics/track';

/** Stable test selector for the post-consume in-session evidence quota toast. */
export const EVIDENCE_QUOTA_TOAST_TEST_ID = 'evidence-quota-toast';

export interface FireEvidenceQuotaConsumedToastInput
  extends Omit<EvidenceQuotaConsumedToastBodyProps, 'isPracticeUpgradeTarget'> {
  /**
   * Whether the user's upgrade path is Practice rather than Clinician.
   * Resolved at the call site (`useChatStream` reads it from billing-info +
   * reverse-trial; dev tools pass an explicit override per chip) so this
   * non-React helper can pick the correct toast tone and propagate it to
   * body + action.
   */
  isPracticeUpgradeTarget: boolean;
  /** User billing tier (`new_product_tier`) for analytics `current_plan`. */
  currentPlan?: SubscriptionTier | null;
}

function trackEvidenceQuotaConsumedToastAnalytics(
  creditsRemaining: number,
  isPracticeUpgradeTarget: boolean,
  currentPlan?: SubscriptionTier | null
): void {
  const creditsProps = {
    credits_remaining: creditsRemaining,
    target_plan: isPracticeUpgradeTarget
      ? SubscriptionTier.CLINICIAN_PRO_PLUS
      : SubscriptionTier.CLINICIAN_PRO,
    ...(currentPlan !== undefined &&
      currentPlan !== null && { current_plan: currentPlan }),
  };

  track({
    event_name: TrackingEventName.CreditsConsumed,
    props: creditsProps,
    options: { amplitude: false },
  });

  track({ options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.EvidenceCreditConsumedToast,
      ...creditsProps,
    },
  });
}

/**
 * Fire the post-consume in-session evidence quota System Toast.
 *
 * Called from `useChatStream` after the BE has already debited the user's
 * quota and emitted the `in_session_evidence_quota_update` SSE chunk. The
 * helper itself does NO eligibility checks — the caller decides whether to
 * fire (current rule: `kind === 'advanced'` && `consumed`).
 *
 * Uses the design-system toast's first-class `pro` / `practice` tones for
 * tier colouring (border + muted bg + tier text + `TierProFilled` /
 * `TierPracticeFilled` icon) and `ToastActionButton` for the Upgrade pill
 * recipe (Figma nodes `10492:57298` Clinician / `10742:53227` Practice).
 *
 * The tier-aware credit headline is rendered in the toast's `title` slot
 * and the reset-date caption in the `description` slot.
 *
 * Returns the sonner toast id so callers can deduplicate if needed.
 */
export function fireEvidenceQuotaConsumedToast(
  input: FireEvidenceQuotaConsumedToastInput
): string | number {
  const { advancedUsed, currentPeriodEnd, isPracticeUpgradeTarget, currentPlan } =
    input;
  const creditsRemaining = Math.max(
    0,
    ADVANCED_EVIDENCE_IN_SESSION_QUOTA - advancedUsed
  );
  trackEvidenceQuotaConsumedToastAnalytics(
    creditsRemaining,
    isPracticeUpgradeTarget,
    currentPlan
  );

  return toast({
    'data-testid': EVIDENCE_QUOTA_TOAST_TEST_ID,
    variant: 'outline',
    tone: isPracticeUpgradeTarget ? 'practice' : 'pro',
    title: (
      <EvidenceQuotaConsumedToastBody
        advancedUsed={advancedUsed}
        isPracticeUpgradeTarget={isPracticeUpgradeTarget}
      />
    ),
    description: (
      <EvidenceQuotaConsumedToastResetCaption currentPeriodEnd={currentPeriodEnd} />
    ),
    action: (
      <EvidenceQuotaConsumedToastUpgradeButton
        isPracticeUpgradeTarget={isPracticeUpgradeTarget}
      />
    ),
  });
}
